import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import {
  faExclamationTriangle,
  faPlus,
} from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import type { CSSProperties } from 'react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ScoreStatus } from '@sorare/core/src/__generated__/globalTypes';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  LinkBox,
  LinkOther,
  LinkOverlay,
} from '@sorare/core/src/atoms/navigation/Box';
import { BrandS, LabelM, LabelS } from '@sorare/core/src/atoms/typography';
import { PlayerScore } from '@sorare/core/src/components/scoring/PlayerScore';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import {
  ensureHexColor,
  getRgbColorFromHex,
} from '@sorare/core/src/lib/colors';
import { playerUnavailability } from '@sorare/core/src/lib/glossary';
import { anyPositionShortNames } from '@sorare/core/src/lib/players';

import { AnyPlayerStatsIcon } from '@sorare/marketplace/src/components/player/AnyPlayerStatsIcon';
import { Grade } from '@sorare/marketplace/src/components/player/Grade';

import { EditFirstLineupWarningDialog } from '#src/components/onboarding/FirstLineup/FirstLineupEdition/FirstLineupPreview/EditFirstLineupDialog/EditFirstLineupWarningDialog/index.tsx';
import { FirstLineupPlayerPrice } from '#src/components/onboarding/FirstLineup/FirstLineupEdition/FirstLineupPreview/FirstLineupPlayerPrice/index.tsx';

import type { PlayerRow_anyPlayerInterface } from './__generated__/index.graphql.ts';

const Root = styled(Horizontal).attrs({ gap: 2 })`
  text-align: left;
  border-radius: var(--double-unit);
  padding-right: var(--unit);
  &.clickable:hover {
    background-color: var(--c-nd-50);
  }
  transition: 100ms background-color ease-in-out;
`;
const Middle = styled(Vertical).attrs({ gap: 0.5 })`
  flex: 1;
`;
const Right = styled(Vertical).attrs({ gap: 2 })`
  align-items: flex-end;
`;
const PlayerImgWrapper = styled(Horizontal).attrs({ center: true })`
  position: relative;
  width: 80px;
  height: 100px;
  border-radius: var(--double-unit);
  background: linear-gradient(
    to bottom,
    var(--c-black),
    rgba(var(--rgb-color), 0.5)
  );
  overflow: hidden;
`;
const PlayerImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: bottom;
  transform: scale(1.25);
`;
const StatsIconWrapper = styled.div`
  position: absolute;
  bottom: var(--unit);
  right: var(--unit);
`;
type Props = {
  player: PlayerRow_anyPlayerInterface;
  showAddButton?: boolean;
  showProjection?: boolean;
  onSelect?: () => void;
  onClickStatsIcon: () => void;
};
export const PlayerRow = ({
  player,
  showAddButton,
  showProjection,
  onSelect,
  onClickStatsIcon,
}: Props) => {
  const { toMonetaryAmount } = useMonetaryAmount();
  const [showWarningDialog, setShowWarningDialog] = useState<
    'noGame' | 'injury' | false
  >(false);

  const {
    activeClub,
    firstName,
    lastName,
    activeInjuries,
    squaredPictureUrl,
    anyCardWithLivePrimaryOffer: card,
  } = player;

  if (!card) return null;

  const price = toMonetaryAmount(card.livePrimaryOffer?.price);
  const { styleGuideColor } = activeClub || {};
  const rgbClubColor = styleGuideColor
    ? getRgbColorFromHex(ensureHexColor(styleGuideColor))
    : '0, 0, 0';
  const displayedFirstName = lastName.length
    ? `${firstName.at(0)}.`
    : firstName;

  const { eligiblePlayerGameScores } = card;
  const [injury] = activeInjuries;
  const isOut = !!injury?.status && ['OFS', 'OUT'].includes(injury.status);
  const noGame =
    !eligiblePlayerGameScores.length ||
    eligiblePlayerGameScores.every(
      ({ scoreStatus }) => scoreStatus === ScoreStatus.NO_GAME
    );

  const { projection } =
    eligiblePlayerGameScores.find(
      ({ projection: proj }) => typeof proj?.grade === 'string'
    ) || {};

  const onClick = () => {
    if (!!injury || !!noGame) {
      setShowWarningDialog(injury ? 'injury' : 'noGame');
      return;
    }
    onSelect?.();
  };

  return (
    <>
      <LinkBox>
        <Root className={classNames({ clickable: !!onSelect })}>
          <PlayerImgWrapper
            style={
              {
                '--rgb-color': rgbClubColor,
              } as CSSProperties
            }
          >
            <PlayerImg src={squaredPictureUrl} />
            <StatsIconWrapper>
              <LinkOther>
                <AnyPlayerStatsIcon
                  player={player}
                  onClick={onClickStatsIcon}
                  backgroundColor="dark"
                />
              </LinkOther>
            </StatsIconWrapper>
          </PlayerImgWrapper>
          <Middle>
            <ConditionalWrapper
              wrap={!!onSelect}
              Wrapper={LinkOverlay}
              props={{ onClick }}
            >
              <BrandS>
                {displayedFirstName} {lastName}
              </BrandS>
            </ConditionalWrapper>
            <Horizontal gap={0.5}>
              {!!activeClub && (
                <>
                  {!!activeClub.pictureUrl && (
                    <img src={activeClub.pictureUrl} height={24} alt="" />
                  )}
                  <LabelM color="var(--c-nd-600)">{activeClub.code}</LabelM>
                </>
              )}
              <LabelM color="var(--c-nd-600)">-</LabelM>
              {!!card.anyPositions.at(0) && (
                <LabelM color="var(--c-nd-600)">
                  <FormattedMessage
                    {...anyPositionShortNames[card.anyPositions.at(0)!]}
                  />
                </LabelM>
              )}
            </Horizontal>
            <Horizontal gap={0.5}>
              {showProjection ? (
                <Grade projection={projection} />
              ) : (
                <>
                  <LabelM>
                    <FormattedMessage
                      id="PlayerData.avg"
                      defaultMessage="Avg."
                    />
                  </LabelM>
                  <PlayerScore
                    player={player}
                    mode="LAST_FIFTEEN_SO5_AVERAGE_SCORE"
                    shape="PILL"
                  />
                </>
              )}
            </Horizontal>
          </Middle>
          <Right>
            <Vertical>
              {!!injury && (
                <Horizontal center gap={0.5}>
                  <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    color={isOut ? 'var(--c-red-400)' : 'var(--c-yellow-400)'}
                    width={12}
                  />

                  <>
                    {injury.status ? (
                      <LabelS
                        color={
                          isOut ? 'var(--c-red-300)' : 'var(--c-yellow-400)'
                        }
                      >
                        {injury.status}
                      </LabelS>
                    ) : (
                      <LabelS color="var(--c-yellow-400)">
                        <FormattedMessage
                          {...playerUnavailability.injuryTitle}
                        />
                      </LabelS>
                    )}
                  </>
                </Horizontal>
              )}
              {!!noGame && (
                <Horizontal center gap={0.5}>
                  <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    color="var(--c-yellow-400)"
                    width={12}
                  />
                  <LabelS color="var(--c-yellow-400)">
                    <FormattedMessage
                      id="EditFirstLineupDialog.PlayerRow.noGame"
                      defaultMessage="No match this week"
                    />
                  </LabelS>
                </Horizontal>
              )}
            </Vertical>
            <Horizontal gap={1.5}>
              {!price.isZero && (
                <FirstLineupPlayerPrice price={price} rightAligned />
              )}
              {!!showAddButton && (
                <IconButton
                  size="small"
                  color="secondary"
                  icon={faPlus}
                  asSpan
                />
              )}
            </Horizontal>
          </Right>
        </Root>
      </LinkBox>
      {!!showWarningDialog && (
        <EditFirstLineupWarningDialog
          open
          type={showWarningDialog}
          onClose={() => {
            setShowWarningDialog(false);
          }}
          onContinue={() => {
            onSelect?.();
          }}
        />
      )}
    </>
  );
};

PlayerRow.fragments = {
  anyPlayerInterface: gql`
    fragment PlayerRow_anyPlayerInterface on AnyPlayerInterface {
      slug
      firstName
      lastName
      squaredPictureUrl
      activeClub {
        slug
        code
        pictureUrl
        styleGuideColor
      }
      activeInjuries {
        id
        status
      }
      anyCardWithLivePrimaryOffer(rarity: $rarity) {
        slug
        anyPositions
        livePrimaryOffer {
          id
          price {
            ...useMonetaryAmount_monetaryAmount
          }
        }
        eligiblePlayerGameScores(so5LeaderboardSlug: $so5LeaderboardSlug) {
          id
          scoreStatus
          projection {
            id
            ...Grade_playerGameScoreProjection
          }
        }
      }
      ...PlayerScore_anyPlayer
      ...AnyPlayerStatsIcon_anyPlayer
    }
    ${PlayerScore.fragments.anyPlayer}
    ${Grade.fragments.projection}
    ${useMonetaryAmount.fragments.monetaryAmount}
    ${AnyPlayerStatsIcon.fragments.anyPlayer}
  ` as TypedDocumentNode<PlayerRow_anyPlayerInterface>,
};
